import { Link } from "@inertiajs/react"
import { Skull } from "lucide-react"

import { EmptyState } from "@/components/empty-state"
import { Heading } from "@/components/heading"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { leagueMatchPath } from "@/routes"
import type { MatchAppearance } from "@/types"

function ResultCell({
  result,
  score,
}: {
  result: MatchAppearance["result"]
  score: string
}) {
  const label = `${score} ${result === "win" ? "W" : result === "draw" ? "D" : "L"}`
  const style =
    result === "win"
      ? {
          background: "oklch(0.25 0.08 145)",
          color: "oklch(0.75 0.15 145)",
          border: "1px solid oklch(0.35 0.10 145)",
        }
      : result === "draw"
        ? {
            background: "oklch(0.25 0.06 55)",
            color: "oklch(0.70 0.10 55)",
            border: "1px solid oklch(0.35 0.08 55)",
          }
        : {
            background: "oklch(0.22 0.06 28)",
            color: "oklch(0.60 0.15 28)",
            border: "1px solid oklch(0.32 0.10 28)",
          }
  return (
    <span className="px-1.5 py-0.5 text-xs font-bold uppercase" style={style}>
      {label}
    </span>
  )
}

interface MatchHistoryProps {
  leagueId: string
  appearances: MatchAppearance[]
}

export function MatchHistory({ leagueId, appearances }: MatchHistoryProps) {
  const totals = appearances.reduce(
    (acc, app) => ({
      touchdowns: acc.touchdowns + app.touchdowns,
      casualties: acc.casualties + app.casualties,
      completions: acc.completions + app.completions,
      mvp: acc.mvp + (app.mvp ? 1 : 0),
      spp: acc.spp + app.spp,
    }),
    { touchdowns: 0, casualties: 0, completions: 0, mvp: 0, spp: 0 },
  )

  return (
    <Card className="py-0">
      <CardContent className="pt-5 pb-4">
        <Heading level={2} wrapperClassName="mb-4">
          Match History
        </Heading>

        {appearances.length === 0 ? (
          <EmptyState
            icon={Skull}
            title="No matches played yet"
            description="Match history will appear here after matches are completed."
          />
        ) : (
          <>
            {/* Header row */}
            <div
              className="flex items-center gap-2 border-b-2 px-3 pb-2"
              style={{ borderColor: "var(--border-heavy)" }}
            >
              <div
                className="w-14 text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                Round
              </div>
              <div
                className="flex-1 text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                Opponent
              </div>
              <div
                className="w-16 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                Result
              </div>
              <div
                className="w-10 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                TD
              </div>
              <div
                className="w-10 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                Cas
              </div>
              <div
                className="w-10 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                Comp
              </div>
              <div
                className="w-10 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                MVP
              </div>
              <div
                className="w-12 text-center text-xs font-bold tracking-widest uppercase"
                style={{ color: "var(--accent-dim)" }}
              >
                SPP
              </div>
            </div>

            {/* Match rows */}
            {appearances.map((app, idx) => (
              <Link
                key={app.match_id ?? idx}
                href={leagueMatchPath(leagueId, app.match_id)}
                className="border-border flex items-center gap-2 border-b px-3 py-2.5 no-underline transition-[background] duration-100 hover:bg-[oklch(0.1_0.005_230)]"
                style={{ color: "inherit" }}
              >
                <div
                  className="w-14 text-xs font-bold"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Rd {app.round_number}
                </div>
                <div className="flex-1 text-sm font-semibold">
                  {app.opponent_name}
                </div>
                <div className="w-16 text-center">
                  <ResultCell result={app.result} score={app.score} />
                </div>
                <div
                  className={cn(
                    "w-10 text-center text-sm",
                    app.touchdowns > 0 && "font-semibold",
                  )}
                  style={
                    app.touchdowns === 0
                      ? { color: "var(--muted-foreground)" }
                      : undefined
                  }
                >
                  {app.touchdowns}
                </div>
                <div
                  className={cn(
                    "w-10 text-center text-sm",
                    app.casualties > 0 && "font-semibold",
                  )}
                  style={
                    app.casualties === 0
                      ? { color: "var(--muted-foreground)" }
                      : undefined
                  }
                >
                  {app.casualties}
                </div>
                <div
                  className={cn(
                    "w-10 text-center text-sm",
                    app.completions > 0 && "font-semibold",
                  )}
                  style={
                    app.completions === 0
                      ? { color: "var(--muted-foreground)" }
                      : undefined
                  }
                >
                  {app.completions}
                </div>
                <div
                  className="w-10 text-center text-sm"
                  style={{
                    color: app.mvp
                      ? "var(--accent)"
                      : "var(--muted-foreground)",
                  }}
                >
                  {app.mvp ? "★" : "—"}
                </div>
                <div
                  className="w-12 text-center text-sm font-bold"
                  style={{ color: "var(--accent)" }}
                >
                  {app.spp}
                </div>
              </Link>
            ))}

            {/* Totals row */}
            <div
              className="mt-1 flex items-center gap-2 border-t-2 px-3 pt-3"
              style={{ borderColor: "var(--border-heavy)" }}
            >
              <div className="w-14" />
              <div className="font-display text-gold-sm flex-1 text-base leading-none font-bold">
                Totals
              </div>
              <div className="w-16" />
              <div
                className="w-10 text-center text-sm font-bold"
                style={{ color: "var(--accent)" }}
              >
                {totals.touchdowns}
              </div>
              <div
                className="w-10 text-center text-sm font-bold"
                style={{ color: "var(--accent)" }}
              >
                {totals.casualties}
              </div>
              <div
                className="w-10 text-center text-sm font-bold"
                style={{ color: "var(--accent)" }}
              >
                {totals.completions}
              </div>
              <div
                className="w-10 text-center text-sm font-bold"
                style={{ color: "var(--accent)" }}
              >
                {totals.mvp}
              </div>
              <div
                className="w-12 text-center text-sm font-bold"
                style={{ color: "var(--accent)" }}
              >
                {totals.spp}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
